// src/pages/Turnos/Turnos.jsx
import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Stack,
  Autocomplete,
  TextField,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { DataGrid, esES } from "@mui/x-data-grid";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import Header from "../../components/Header";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import { getDriversByBoss } from "../../services/driverService";
import {
  getTurnosByDrivers,
  getMisTurnos,
  deleteTurnoById,
  updateTurnoById,
  getTurnosSummary,
} from "../../services/turnoService";
import { crearLiquidacionDesdeTurnos } from "../../services/accountingClosureService";
import { getStoredUserType } from "../../services/authService";
import * as XLSX from "xlsx";

/* ===== Paletas coherentes con el resto ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

const formatLocalDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const resolveLicenseLabel = (license) => {
  if (!license) return "-";
  if (typeof license === "object") {
    return license.licenseNumber || license.number || license.license || license._id || "-";
  }
  return license;
};

/* ===== Widget reutilizable ===== */
function StatCard({ icon, label, value, palette, highlight = false, isMobile = false }) {
  return (
    <Box
      sx={{
        p: isMobile ? 1.75 : 2,
        borderRadius: "12px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: isMobile ? "center" : "flex-start",
        gap: isMobile ? 1 : 1.25,
        minHeight: 76,
        height: "100%",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2,6,23,0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Box sx={{ opacity: 0.95, display: "grid", placeItems: "center" }}>{icon}</Box>
      <Box sx={{ flex: 1, textAlign: isMobile ? "center" : "left" }}>
        <Typography
          variant="caption"
          sx={{ color: palette.label, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

/* ===== Card contenedora ===== */
function Card({ title, palette, children, action }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "14px",
        p: 1.5,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 8px 22px rgba(2,6,23,0.08)"
            : "0 3px 10px rgba(0,0,0,0.24)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0.5,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
          {title}
        </Typography>
        {action && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
            {action}
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex", flexDirection: "column" }}>
        {children}
      </Box>
    </Box>
  );
}

const Turnos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const isMobile = useMediaQuery("(max-width:900px)");
  const userType = useSelector((state) => state.user.type) || getStoredUserType();
  const storedUser = useMemo(() => {
    try {
      const saved = localStorage.getItem("userData");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  }, []);
  const userId = useSelector((state) => state.user.userId) || storedUser.userId || null;
  const driverId = useSelector((state) => state.user.driverId) || storedUser.driverId || null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRows, setSelectedRows] = useState([]); // ids seleccionados
  const [selectedDriver, setSelectedDriver] = useState(null); // {_id, name}
  const [drivers, setDrivers] = useState([]); // [{_id, name}]
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    return formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [dateTo, setDateTo] = useState(() => formatLocalDate(new Date()));
  const [summary, setSummary] = useState({
    hojasPorLiquidar: 0,
    recaudacionPorLiquidar: 0,
    balanceEmpresa: 0,
    balanceConductor: 0,
  });
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({
    startKm: "",
    endKm: "",
    startIncome: "",
    endIncome: "",
    cardIncome: "",
    creditIncome: "",
    gas: "",
    date: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const driverMap = useMemo(() => {
    const map = new Map();
    (drivers || []).forEach((d) => map.set(d._id, d.name || d.fullName || d.username || "Sin nombre"));
    return map;
  }, [drivers]);
  const openNativeDatePicker = useCallback((event) => {
    const input = event.target;
    if (typeof input?.showPicker === "function") {
      try {
        input.showPicker();
      } catch {
        // Algunos navegadores piden gesto explícito de usuario.
      }
    }
  }, []);

  const toDateInput = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  };

  useEffect(() => {
    if (location.state?.csvIntent) {
      console.log("[CsvIntent] Turnos scene received payload", location.state.csvIntent);
    }
  }, [location.state]);

  // Fetch drivers + closures
  useEffect(() => {
    const fetchClosures = async () => {
      try {
        setLoading(true);
        setError("");
        let drv = [];
        let closures = [];
        let targetDriverIds = [];

        if (userType === "Boss") {
          drv = await getDriversByBoss(); // [{_id, name, ...}]
          const driverIds = (drv || []).map((d) => d._id);
          const normalizedDrivers = (drv || []).map((d) => ({
            _id: d._id,
            name: d.name || d.fullName || d.username || "Sin nombre",
          }));
          setDrivers(normalizedDrivers);
          targetDriverIds = selectedDriver ? [selectedDriver._id] : driverIds;
          const idsToQuery = targetDriverIds.length ? targetDriverIds : driverIds;
          closures = idsToQuery.length
            ? await getTurnosByDrivers(idsToQuery, userId, {
                startDate: dateFrom || undefined,
                endDate: dateTo || undefined,
              })
            : [];
        } else {
          targetDriverIds = driverId ? [driverId] : [];
          closures = await getMisTurnos({
            driverId,
            userId,
            startDate: dateFrom || undefined,
            endDate: dateTo || undefined,
          });
        }

        const localDriverMap = new Map(
          (drv || drivers || []).map((d) => [d._id, d.name || d.fullName || d.username || "Sin nombre"])
        );

        const mapped = (closures || [])
          .filter((c) => !c.liquidado)
          .map((c, idx) => {
            const factServicios = Number(c.facturacionServicios || 0);
            const factRes = Number.isFinite(Number(c.facturacionResuelta))
              ? Number(c.facturacionResuelta)
              : NaN;
            const facturacionResuelta =
              Number.isFinite(factRes) && (factRes !== 0 || factServicios === 0)
                ? factRes
                : factServicios ||
                  Number(c.facturacionTotal || 0) ||
                  Number(c.endIncome || 0) - Number(c.startIncome || 0);
            return {
              id: c._id || `srv_${idx}`,
              driverId: c.driver?._id || c.driverId || "",
              driverName:
                c.driver?.name ||
                c.driverName ||
                localDriverMap.get(c.driver?._id || c.driverId) ||
                storedUser.name ||
                storedUser.fullName ||
                storedUser.username ||
                "Sin nombre",
              licencia: resolveLicenseLabel(c.license || c.licenseId),
              facturacion: facturacionResuelta.toFixed(2),
              tarjeta: Number(c.cardIncome || 0).toFixed(2),
              credito: Number(c.creditIncome || 0).toFixed(2),
              gasolina: Number(c.gas || 0).toFixed(2),
              kmTomar: c.startKm ?? 0,
              kmDejar: c.endKm ?? 0,
              recaudacionTomar: Number(c.startIncome || 0).toFixed(2),
              recaudacionDejar: Number(c.endIncome || 0).toFixed(2),
              fecha: c.date || c.fecha || c.createdAt || null,
              facturacionDiscrepancia: Boolean(c.facturacionDiscrepancia),
            };
          });

        setRows(mapped);

        try {
          const summaryData = await getTurnosSummary({
            driverIds: targetDriverIds,
            startDate: dateFrom || undefined,
            endDate: dateTo || undefined,
            liquidado: false,
          });
          setSummary(summaryData);
        } catch (sumErr) {
          console.warn("No se pudo cargar resumen de turnos", sumErr?.message);
          setSummary({
            hojasPorLiquidar: mapped.length,
            recaudacionPorLiquidar: mapped.reduce(
              (sum, r) => sum + parseFloat(r.recaudacionDejar || 0),
              0
            ),
            balanceEmpresa: mapped.reduce(
              (sum, r) => sum + (parseFloat(r.recaudacionDejar || 0) - parseFloat(r.gasolina || 0)),
              0
            ),
            balanceConductor: 0,
          });
        }
      } catch (err) {
        console.error("Error cargando turnos:", err);
        setError(err.message || "No se pudieron cargar las hojas de turno");
      } finally {
        setLoading(false);
      }
    };

    fetchClosures();
  }, [userType, userId, driverId, dateFrom, dateTo, selectedDriver]);

  // Opciones del selector (API o derivadas del mock/rows)
  const driverOptions = useMemo(() => {
    const base = drivers.length
      ? drivers
      : Array.from(new Map(rows.map((r) => [r.driverId, { _id: r.driverId, name: r.driverName }])).values());
    return base;
  }, [drivers, rows]);

  // Filtrado de filas por conductor
  const filteredRows = useMemo(() => {
    if (!selectedDriver) return rows;
    return rows.filter((r) => r.driverId === selectedDriver._id);
  }, [rows, selectedDriver]);

  const handleEditChange = (field) => (event) => {
    setEditForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveEdit = async () => {
    if (!editRow) return;
    try {
      setSavingEdit(true);
      const payload = {
        startKm: Number(editForm.startKm),
        endKm: Number(editForm.endKm),
        startIncome: Number(editForm.startIncome),
        endIncome: Number(editForm.endIncome),
        cardIncome: Number(editForm.cardIncome || 0),
        creditIncome: Number(editForm.creditIncome || 0),
        gas: Number(editForm.gas || 0),
      };
      if (editForm.date) payload.date = new Date(editForm.date);
      await updateTurnoById(editRow.id, payload);
      setRows((prev) =>
        prev.map((r) =>
          r.id === editRow.id
            ? {
                ...r,
                kmTomar: payload.startKm,
                kmDejar: payload.endKm,
                recaudacionTomar: payload.startIncome,
                recaudacionDejar: payload.endIncome,
                tarjeta: payload.cardIncome,
                credito: payload.creditIncome,
                gasolina: payload.gas,
                facturacion: (payload.endIncome || 0) - (payload.startIncome || 0),
                fecha: editForm.date || r.fecha,
              }
            : r
        )
      );
      setEditRow(null);
    } catch (err) {
      setError(err?.message || "No se pudo actualizar el turno.");
    } finally {
      setSavingEdit(false);
    }
  };

  // Widgets (usa resumen del backend; fallback al filtrado)
  const hojasPorLiquidar = summary.hojasPorLiquidar ?? filteredRows.length;
  const recaudacionPorLiquidar =
    summary.recaudacionPorLiquidar != null
      ? Number(summary.recaudacionPorLiquidar || 0).toFixed(2)
      : filteredRows.reduce((sum, r) => sum + parseFloat(r.recaudacionDejar || 0), 0).toFixed(2);
  const balanceEmpresa =
    summary.balanceEmpresa != null
      ? Number(summary.balanceEmpresa || 0).toFixed(2)
      : filteredRows
          .reduce((sum, r) => sum + parseFloat(r.recaudacionDejar || 0) - parseFloat(r.gasolina || 0), 0)
          .toFixed(2);

  // Validación al liquidar
  const liquidarHojas = async () => {
    if (!selectedRows.length) {
      alert("Selecciona al menos una hoja para liquidar.");
      return;
    }
    const first = filteredRows.find((r) => r.id === selectedRows[0]);
    const sameDriver = selectedRows.every((id) => {
      const r = filteredRows.find((x) => x.id === id);
      return r && r.driverId === first?.driverId;
    });
    if (!sameDriver) {
      alert("Solo puedes liquidar hojas del mismo conductor.");
      return;
    }
    try {
      setLoading(true);
      const liq = await crearLiquidacionDesdeTurnos(selectedRows);
      setRows((prev) => prev.filter((r) => !selectedRows.includes(r.id)));
      setSelectedRows([]);
      alert(
        `Liquidación creada con ${selectedRows.length} hojas para ${first?.driverName || "el conductor seleccionado"}.`
      );
      console.log("[WEB][Turnos] Liquidación creada", liq?._id);
    } catch (err) {
      console.error("Error al liquidar hojas:", err);
      setError(err?.message || "No se pudo liquidar las hojas seleccionadas.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmId) return;
    try {
      setDeleting(true);
      await deleteTurnoById(confirmId);
      setRows((prev) => prev.filter((r) => r.id !== confirmId));
    } catch (err) {
      console.error("Error eliminando turno:", err);
      setError(err?.message || "No se pudo eliminar el turno.");
    } finally {
      setDeleting(false);
      setConfirmId(null);
    }
  };

  const formatDateValue = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
  };

  const handleExportExcel = () => {
    const exportableColumns = columnasTurnos.filter(
      (col, idx, arr) =>
        col.field !== "acciones" && arr.findIndex((c) => c.field === col.field) === idx
    );
    const numericFields = [
      "facturacion",
      "tarjeta",
      "credito",
      "gasolina",
      "kmTomar",
      "kmDejar",
      "recaudacionTomar",
      "recaudacionDejar",
    ];

    const grouped = filteredRows.reduce((acc, row) => {
      const key = row.driverId || row.driverName || "Sin conductor";
      if (!acc.has(key)) acc.set(key, []);
      acc.get(key).push(row);
      return acc;
    }, new Map());

    const data = [];

    grouped.forEach((rowsGroup, driverKey) => {
      const driverName = rowsGroup[0]?.driverName || driverKey || "Sin conductor";

      rowsGroup.forEach((row) => {
        const record = {};
        exportableColumns.forEach((col) => {
          let value = row[col.field];
          if (col.field === "fecha") value = formatDateValue(row.fecha);
          record[col.headerName] = value ?? "";
        });
        data.push(record);
      });

      const subtotal = numericFields.reduce((acc, field) => {
        acc[field] = rowsGroup.reduce((sum, r) => sum + Number(r[field] || 0), 0);
        return acc;
      }, {});

      const totalRow = {};
      exportableColumns.forEach((col) => {
        if (col.field === "driverName") {
          totalRow[col.headerName] = `Total ${driverName}`;
        } else if (numericFields.includes(col.field)) {
          totalRow[col.headerName] = subtotal[col.field].toFixed(2);
        } else {
          totalRow[col.headerName] = "";
        }
      });
      data.push(totalRow);

      data.push({});
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Hojas de turno");
    const dateSuffix = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `hojas-turno-${dateSuffix}.xlsx`);
  };

  // Columnas DataGrid — menú de acciones compacto
  const ActionMenu = ({ rowId }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    return (
      <>
        <IconButton
          aria-label="Acciones"
          size="small"
          onClick={handleOpen}
          sx={{ color: palette.text }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              navigate(`/turnos/${rowId}`);
            }}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Más detalles" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose();
              const row = rows.find((r) => r.id === rowId);
              if (row) {
                setEditRow(row);
                setEditForm({
                  startKm: row.kmTomar ?? "",
                  endKm: row.kmDejar ?? "",
                  startIncome: row.recaudacionTomar ?? "",
                  endIncome: row.recaudacionDejar ?? "",
                  cardIncome: row.tarjeta ?? "",
                  creditIncome: row.credito ?? "",
                  gas: row.gasolina ?? "",
                  date: toDateInput(row.fecha || row.date || row.createdAt),
                });
              }
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Editar" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose();
              setConfirmId(rowId);
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Eliminar" />
          </MenuItem>
        </Menu>
      </>
    );
  };

  const columnasTurnos = [
    { field: "driverName", headerName: "Conductor", flex: 1 },
    {
      field: "fecha",
      headerName: "Fecha",
      flex: 1,
      valueFormatter: (params) => formatDateValue(params.value),
    },
    { field: "licencia", headerName: "Licencia", flex: 1 },
    { field: "facturacion", headerName: "Facturación (€)", flex: 1 },
    { field: "tarjeta", headerName: "Cobro Tarjeta (€)", flex: 1 },
    { field: "credito", headerName: "Servicio Crédito (€)", flex: 1 },
    { field: "gasolina", headerName: "Gasolina (€)", flex: 1 },
    { field: "kmTomar", headerName: "Km al Tomar", flex: 1 },
    { field: "kmDejar", headerName: "Km al Dejar", flex: 1 },
    { field: "recaudacionTomar", headerName: "Recaudación al Tomar (€)", flex: 1 },
    { field: "recaudacionDejar", headerName: "Recaudación al Dejar (€)", flex: 1 },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 0.45,
      minWidth: 52,
      sortable: false,
      filterable: false,
      renderCell: (params) => <ActionMenu rowId={params.row.id} />,
    },
  ];

  // Restringe selección a mismo conductor
  const isRowSelectable = (params) => {
    const row = params.row;
    if (selectedDriver) return row.driverId === selectedDriver._id;
    if (selectedRows.length === 0) return true;
    const first = filteredRows.find((r) => r.id === selectedRows[0]);
    return row.driverId === first?.driverId;
  };

  return (
    <>
    <Box
      sx={{
        m: { xs: "12px", md: "20px" },
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Header title="Hojas de Turno" subtitle="Listado de todas las hojas de turno" />
      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* ===== 1) Widgets ARRIBA ===== */}
      <Box
        sx={{
          mt: 1,
          mb: 1.25,
          display: "flex",
          gap: 2,
          width: "100%",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          alignItems: "stretch",
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <StatCard
            icon={<TrendingUpIcon />}
            label="Hojas por liquidar"
            value={hojasPorLiquidar}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <StatCard
            icon={<AttachMoneyIcon />}
            label="Recaudación por liquidar"
            value={`€${recaudacionPorLiquidar}`}
            palette={palette}
            highlight
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <StatCard
            icon={<AccountBalanceIcon />}
            label="Balance empresa"
            value={`€${balanceEmpresa}`}
            palette={palette}
            isMobile={isMobile}
          />
        </Box>
      </Box>

      {/* ===== 2) Barra de FILTRO ARRIBA de la tabla ===== */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        gap={1.5}
        sx={{ mb: 1.5 }}
      >
        <Autocomplete
          size="small"
          options={driverOptions}
          getOptionLabel={(o) => o?.name || ""}
          value={selectedDriver}
          onChange={(_, v) => {
            setSelectedDriver(v);
            setSelectedRows([]); // limpia selección al cambiar filtro
          }}
          renderInput={(params) => (
            <TextField {...params} label="Filtrar por conductor" placeholder="Selecciona un conductor" />
          )}
          sx={{
            minWidth: 260,
            "& .MuiOutlinedInput-root": {
              "& .MuiOutlinedInput-input": { color: palette.text },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
            },
            "& .MuiInputLabel-root": { color: palette.label },
            "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
            "& .MuiOutlinedInput-input::placeholder": { color: palette.label, opacity: 0.8 },
            "& .MuiAutocomplete-popupIndicator": { color: palette.text },
          }}
          clearOnEscape
        />
        <TextField
          label="Desde"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 160,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
            },
            "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
          }}
          inputProps={{
            onClick: openNativeDatePicker,
          }}
        />
        <TextField
          label="Hasta"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 160,
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
            },
            "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
          }}
          inputProps={{
            onClick: openNativeDatePicker,
          }}
        />
      </Stack>

      {/* ===== 3) Tabla abajo ===== */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
        }}
      >
      <Card
        title="Hojas de Turno"
        palette={palette}
        action={
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            flexWrap="nowrap"
            justifyContent="flex-start"
            width="100%"
            sx={{ overflowX: "auto" }}
          >
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddCircleIcon />}
              onClick={() => navigate("/turnos/nuevo")}
              size="small"
              sx={{
                fontWeight: 700,
                textTransform: "none",
                whiteSpace: "nowrap",
                textAlign: "center",
                px: { xs: 0.75, sm: 1 },
                minWidth: 0,
                fontSize: { xs: "0.78rem", sm: "0.9rem" },
                lineHeight: 1.1,
              }}
            >
                Añadir turno
            </Button>
              <Chip
                label={`Seleccionadas: ${selectedRows.length}`}
                size="small"
                sx={{
                  bgcolor: "transparent",
                  border: `1px solid ${palette.cardBorder}`,
                  color: palette.text,
                  fontWeight: 600,
                  px: 1,
                  whiteSpace: "nowrap",
                }}
              />
            {!isMobile && (
              <Button
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                onClick={handleExportExcel}
                size="small"
                sx={{
                  fontWeight: 700,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  borderColor: palette.cardBorder,
                  color: palette.text,
                  "&:hover": { borderColor: palette.accent },
                }}
              >
                Exportar a Excel
              </Button>
            )}
            <Button
              variant="contained"
              onClick={liquidarHojas}
              sx={{
                backgroundColor: palette.btn,
                color: "white",
                fontWeight: 700,
                textTransform: "none",
                whiteSpace: "nowrap",
                textAlign: "center",
                px: { xs: 0.8, sm: 1 },
                minWidth: 0,
                fontSize: { xs: "0.78rem", sm: "0.9rem" },
                lineHeight: 1.1,
                "&:hover": { backgroundColor: palette.btnHover },
              }}
            >
                Liquidar seleccionadas
            </Button>
            </Stack>
          }
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: "100%",
              overflowX: "hidden",
              "& .MuiDataGrid-root": {
                border: "none",
                color: palette.text,
                backgroundColor: "transparent",
                height: "100%",
                width: "100%",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "transparent",
                borderBottom: `1px solid ${palette.cardBorder}`,
                color: palette.label,
                fontWeight: 700,
              },
              "& .MuiDataGrid-cell": { borderBottom: "none" },
              "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
              "& .MuiDataGrid-footerContainer": {
                borderTop: `1px solid ${palette.cardBorder}`,
                backgroundColor: "transparent",
              },
              "& .MuiDataGrid-overlay": {
                backgroundColor: "transparent",
                color: palette.label,
              },
              "& .MuiCheckbox-root": { color: `${palette.accent} !important` },
              "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
                width: 38,
                minWidth: 38,
                maxWidth: 38,
              },
            }}
          >
            <DataGrid
              loading={loading}
              checkboxSelection
              rows={filteredRows}
              columns={columnasTurnos}
              columnVisibilityModel={
                isMobile
                  ? {
                      licencia: false,
                      tarjeta: false,
                      credito: false,
                      gasolina: false,
                      kmTomar: false,
                      kmDejar: false,
                      recaudacionTomar: false,
                      recaudacionDejar: false,
                    }
                  : undefined
              }
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableColumnMenu
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
              isRowSelectable={isRowSelectable}
              sx={{ height: "100%", width: "100%" }}
            rowHeight={52}
            columnHeaderHeight={48}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
          />
        </Box>
      </Card>
    </Box>
    </Box>

    <Dialog
      open={Boolean(editRow)}
      onClose={() => setEditRow(null)}
      fullWidth
      maxWidth="sm"
      BackdropProps={{
        sx: {
          backgroundColor: alpha("#0F172A", 0.55),
          backdropFilter: "blur(2px)",
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 2.5,
          color: palette.text,
          boxShadow:
            palette === PALETTE_LIGHT
              ? "0 18px 60px rgba(2,6,23,0.12)"
              : "0 20px 70px rgba(0,0,0,0.55)",
        },
      }}
    >
      <DialogTitle sx={{ color: palette.title, fontWeight: 800, pb: 0 }}>Editar hoja de turno</DialogTitle>
      <DialogContent dividers sx={{ borderColor: alpha(palette.cardBorder, 0.6), pt: 1.5 }}>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <TextField
            label="Fecha"
            type="date"
            value={editForm.date}
            onChange={handleEditChange("date")}
            InputLabelProps={{ shrink: true }}
            sx={{ input: { color: palette.text } }}
          />
          <TextField
            label="Km al tomar"
            type="number"
            value={editForm.startKm}
            onChange={handleEditChange("startKm")}
          />
          <TextField
            label="Km al dejar"
            type="number"
            value={editForm.endKm}
            onChange={handleEditChange("endKm")}
          />
          <TextField
            label="Recaudación al tomar"
            type="number"
            value={editForm.startIncome}
            onChange={handleEditChange("startIncome")}
          />
          <TextField
            label="Recaudación al dejar"
            type="number"
            value={editForm.endIncome}
            onChange={handleEditChange("endIncome")}
          />
          <TextField
            label="Tarjetas de crédito"
            type="number"
            value={editForm.cardIncome}
            onChange={handleEditChange("cardIncome")}
          />
          <TextField
            label="Crédito / Abonados"
            type="number"
            value={editForm.creditIncome}
            onChange={handleEditChange("creditIncome")}
          />
          <TextField
            label="Gasolina"
            type="number"
            value={editForm.gas}
            onChange={handleEditChange("gas")}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={() => setEditRow(null)}
          sx={{ color: palette.text, textTransform: "none", fontWeight: 600 }}
          disabled={savingEdit}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveEdit}
          disabled={savingEdit}
          sx={{ textTransform: "none", fontWeight: 800, backgroundColor: palette.btn, "&:hover": { backgroundColor: palette.btnHover } }}
        >
          {savingEdit ? "Guardando..." : "Guardar cambios"}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={Boolean(confirmId)}
      onClose={() => setConfirmId(null)}
      maxWidth="xs"
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor: alpha("#0F172A", 0.55),
          backdropFilter: "blur(2px)",
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 2.5,
          color: palette.text,
          boxShadow:
            palette === PALETTE_LIGHT
              ? "0 18px 60px rgba(2,6,23,0.12)"
              : "0 20px 70px rgba(0,0,0,0.55)",
        },
      }}
    >
      <DialogTitle sx={{ color: palette.title, fontWeight: 800, pb: 0 }}>
        Eliminar turno
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: alpha(palette.cardBorder, 0.6), pt: 1.5 }}>
        <Typography sx={{ color: palette.text }}>
          ¿Seguro que quieres eliminar esta hoja de turno? Esta acción no se puede deshacer.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button
          onClick={() => setConfirmId(null)}
          sx={{ color: palette.text, textTransform: "none", fontWeight: 600 }}
          disabled={deleting}
        >
          Cancelar
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={handleDelete}
          disabled={deleting}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            px: 2.5,
            backgroundColor: isDark ? "#dc2626" : "#ef4444",
            "&:hover": { backgroundColor: isDark ? "#b91c1c" : "#dc2626" },
          }}
        >
          {deleting ? "Eliminando..." : "Eliminar"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default Turnos;
